import { Button, Skeleton, cn } from "@ndd/ui";
import { FileText, History, RotateCw, WifiOff } from "lucide-react";
import * as React from "react";
import {
  INITIAL_DRAWER_NAV,
  drawerNavReducer,
} from "../../lib/previous-soap-notes-reader";
import { DRAWER_TEXT } from "./drawer-type-roles";
import { PreviousSoapNotesOverview } from "./previous-soap-notes-overview";
import { PreviousSoapNotesReader, WidenButton } from "./previous-soap-notes-reader";
import { SideDrawerHeader } from "./side-drawer-header";
import type { PreviousSoapNotesView } from "./use-previous-soap-notes";
import {
  TOGGLE_OPEN_EDGE,
  UTILITY_ENGAGED_FILL,
  UTILITY_LABEL,
  UTILITY_REST,
} from "./workspace-look";

// Button variant utility at size compact (NO-799): classic the outline button
// at size sm as before; under the theme the 36px utility button, with an olive
// inset while open. Colors read theme-only values (workspace-look.ts).
const TOGGLE_BASE =
  "gap-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const TOGGLE_CLOSED = cn(UTILITY_REST, "hover:border-subtle hover:bg-muted");
const TOGGLE_OPEN = cn(TOGGLE_OPEN_EDGE, UTILITY_ENGAGED_FILL);

/**
 * The controls-row button that opens the drawer (desktop) or the sheet
 * (narrow). One element in every state, so it keeps keyboard focus when it
 * flips between Show and Hide.
 */
export function PreviousSoapNotesToggle({
  open,
  onToggle,
  panelId,
  buttonRef,
}: {
  open: boolean;
  onToggle: () => void;
  /** The id of the drawer (or Sheet) this button opens. */
  panelId?: string;
  buttonRef?: React.Ref<HTMLButtonElement>;
}): React.ReactElement {
  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="utility"
      size="compact"
      aria-expanded={open}
      aria-controls={panelId}
      onClick={onToggle}
      className={cn(TOGGLE_BASE, open ? TOGGLE_OPEN : TOGGLE_CLOSED)}
    >
      <History aria-hidden="true" />
      <span className={cn(DRAWER_TEXT.control, UTILITY_LABEL)}>
        {open ? "Hide previous notes" : "Show previous notes"}
      </span>
    </Button>
  );
}

export interface PreviousSoapNotesPanelProps {
  view: PreviousSoapNotesView;
  timezone: string;
  /** ISO time used for the "2 weeks ago" labels; injected so renders are deterministic. */
  now: string;
  patientName: string | null;
  onSelect: (noteId: string) => void;
  onRetry: () => void;
  onRetrySelection: () => void;
  /** Present when this panel owns its close control (the docked drawer). */
  onClose?: () => void;
  /** The docked drawer stays mounted while hidden; opening it returns to the overview. */
  open?: boolean;
  /** Present only where the drawer can be widened; absent in the Sheet and below 1280px. */
  widen?: { wide: boolean; onToggle: () => void };
}

/**
 * The docked drawer (1024px and wider) or full-height sheet (below) of the
 * last three qualifying Healthie notes. It opens on an overview of the three
 * visits; opening one shows it in the reader, where Newer and Older page
 * through the list. Only the reader reads a note: the overview uses the list
 * response alone.
 */
export function PreviousSoapNotesPanel(
  props: PreviousSoapNotesPanelProps
): React.ReactElement {
  const { view, timezone, now, onSelect, onRetrySelection, onClose } = props;
  const { nav, dispatch, overviewScroll, dateRef } = useDrawerNav(props.open ?? true);

  return (
    <section
      aria-label="Previous SOAP notes"
      className="flex h-full min-h-0 flex-col bg-card"
    >
      {view.status === "ready" && nav.mode === "reader" ? (
        <ReadingVisit
          view={view}
          index={nav.index}
          timezone={timezone}
          now={now}
          onClose={onClose}
          dateRef={dateRef}
          controls={
            props.widen ? (
              <WidenButton wide={props.widen.wide} onToggle={props.widen.onToggle} />
            ) : undefined
          }
          onRetrySelection={onRetrySelection}
          onBack={() => dispatch({ type: "back_to_overview" })}
          onNavigate={(action, noteId) => {
            dispatch(action);
            onSelect(noteId);
          }}
        />
      ) : (
        <>
          <SideDrawerHeader
            title="Previous SOAP notes"
            subtitle="Last 3 notes in Healthie · newest first"
            closeLabel="Close previous SOAP notes"
            onClose={onClose}
          />
          <PreviousSoapNotesBody
            view={view}
            timezone={timezone}
            now={now}
            patientName={props.patientName}
            onRetry={props.onRetry}
            overviewScroll={overviewScroll}
            returnedFrom={nav.originIndex}
            onOpen={(index, noteId) => {
              const overviewScrollTop = overviewScroll.current?.scrollTop ?? 0;
              dispatch({ type: "open_note", index, overviewScrollTop });
              onSelect(noteId);
            }}
          />
        </>
      )}
    </section>
  );
}

/** Overview or reader, reset to the overview whenever the drawer opens. */
function useDrawerNav(open: boolean) {
  const [nav, dispatch] = React.useReducer(drawerNavReducer, INITIAL_DRAWER_NAV);
  const overviewScroll = React.useRef<HTMLDivElement>(null);
  const dateRef = React.useRef<HTMLHeadingElement>(null);

  // Reset on close as well as on open, so a reopened drawer is already on the
  // overview when the heading takes focus.
  React.useEffect(() => {
    dispatch({ type: "open_changed" });
  }, [open]);

  React.useEffect(() => {
    if (nav.mode === "reader") dateRef.current?.focus({ preventScroll: true });
  }, [nav.mode, nav.index]);

  React.useEffect(() => {
    if (nav.mode === "overview" && overviewScroll.current) {
      overviewScroll.current.scrollTop = nav.overviewScrollTop;
    }
  }, [nav.mode, nav.overviewScrollTop]);

  return { nav, dispatch, overviewScroll, dateRef };
}

function ReadingVisit({
  view,
  index,
  timezone,
  now,
  onClose,
  dateRef,
  controls,
  onRetrySelection,
  onBack,
  onNavigate,
}: {
  view: Extract<PreviousSoapNotesView, { status: "ready" }>;
  index: number;
  timezone: string;
  now: string;
  onClose?: (() => void) | undefined;
  dateRef: React.Ref<HTMLHeadingElement>;
  controls?: React.ReactNode;
  onRetrySelection: () => void;
  onBack: () => void;
  onNavigate: (
    action: { type: "newer" } | { type: "older"; total: number },
    noteId: string
  ) => void;
}): React.ReactElement {
  const selectedId =
    view.selection.status === "ready" ? view.selection.note.id : view.selection.noteId;
  const shown = view.notes.findIndex((note) => note.id === selectedId);
  const current = shown >= 0 ? shown : index;
  const newer = view.notes[current - 1];
  const older = view.notes[current + 1];

  return (
    <PreviousSoapNotesReader
      notes={view.notes}
      index={current}
      selection={view.selection}
      timezone={timezone}
      now={now}
      onBack={onBack}
      dateRef={dateRef}
      controls={controls}
      onNewer={() => newer && onNavigate({ type: "newer" }, newer.id)}
      onOlder={() =>
        older && onNavigate({ type: "older", total: view.notes.length }, older.id)
      }
      onRetrySelection={onRetrySelection}
      {...(onClose ? { onClose } : {})}
    />
  );
}

function PreviousSoapNotesBody({
  view,
  timezone,
  now,
  patientName,
  onRetry,
  overviewScroll,
  returnedFrom,
  onOpen,
}: {
  view: PreviousSoapNotesView;
  timezone: string;
  now: string;
  patientName: string | null;
  onRetry: () => void;
  overviewScroll: React.Ref<HTMLDivElement>;
  returnedFrom: number | null;
  onOpen: (index: number, noteId: string) => void;
}): React.ReactElement {
  if (view.status === "loading") {
    return <PreviousNotesLoading />;
  }

  if (view.status === "empty") {
    return (
      <StateBlock icon={FileText} title="No previous notes yet">
        {possessive(patientName)} notes will show up here after they’re signed in
        Healthie.
      </StateBlock>
    );
  }

  if (view.status === "unavailable") {
    return (
      <StateBlock icon={WifiOff} title="Couldn’t load previous notes">
        Healthie didn&apos;t respond. Your appointment note isn&apos;t affected.
        <div className="mt-3 flex justify-center">
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            <RotateCw aria-hidden="true" />
            Try again
          </Button>
        </div>
      </StateBlock>
    );
  }

  return (
    <PreviousSoapNotesOverview
      notes={view.notes}
      timezone={timezone}
      now={now}
      scrollRef={overviewScroll}
      focusIndex={returnedFrom}
      onOpen={(index) => {
        const note = view.notes[index];
        if (note) onOpen(index, note.id);
      }}
    />
  );
}

function PreviousNotesLoading(): React.ReactElement {
  return (
    <div className="space-y-2.5 px-5 pt-5" role="status">
      <Skeleton className="h-2.5 w-4/5" />
      <Skeleton className="h-2.5 w-2/3" />
      <Skeleton className="h-2.5 w-11/12" />
      <Skeleton className="h-2.5 w-1/2" />
      <Skeleton className="h-2.5 w-3/4" />
      <p className={cn(DRAWER_TEXT.stateBody, "pt-5")}>Loading previous notes…</p>
    </div>
  );
}

function StateBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileText;
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center px-6 py-7 text-center">
      <span className="flex size-9 items-center justify-center rounded-full bg-muted">
        <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
      </span>
      <p role="status" className={cn(DRAWER_TEXT.stateTitle, "mt-3")}>
        {title}
      </p>
      <div className={cn(DRAWER_TEXT.stateBody, "mt-1")}>{children}</div>
    </div>
  );
}

function possessive(patientName: string | null): string {
  const first = patientName?.trim().split(/\s+/)[0];
  if (!first) return "This patient’s";

  return first.endsWith("s") ? `${first}’` : `${first}’s`;
}
